import { QueryClient, useMutation } from '@tanstack/react-query';
import { T, useTranslate } from '@tolgee/react';
import { AxiosError } from 'axios';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { getSession, useSession } from 'next-auth/react';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Loader } from '../../components/_atoms/_Loader';
import { Button } from '../../components/_atoms/Button';
import BackgroundBlobs from '../../components/_molecules/BackgroundBlobs/BackgroundBlobs';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import useRouterMatch from '../../hooks/useRouterMatch';
import useSignIn from '../../hooks/useSignIn';
import backendService from '../../services/backendService';
import { detectAndBestEffortSetLanguage } from '../../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../../utils/react-query-utils';
import { provisionFlags } from '../../utils/ServerSideFeatureFlagUtils';

const PinInput = ({
  value,
  onChange,
  onPressEnter,
}: {
  value?: string;
  onChange: (val: string) => void;
  onPressEnter: () => void;
}) => {
  return (
    <Container>
      <Input
        type="tel"
        onChange={e => onChange(e.target.value.replace(/[^\d]/g, '').slice(0, 7))}
        value={value}
        autoFocus
        onKeyPress={e => {
          if (e.key === 'Enter') {
            onPressEnter();
          }
        }}
      />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Input = styled.input`
  border: 1px solid black;
  border-radius: 10px;
  padding: 10px 20px;
  width: 150px;
  font-size: 30px;
  text-align: center;
`;

const Lobby = ({
  onJoin,
  isError,
  error,
  isLoading,
  pin,
  setPin,
  lastSubmittedPin,
}: {
  lastSubmittedPin?: string;
  pin?: string;
  setPin: (pin: string) => void;
  isError: boolean;
  isLoading: boolean;
  onJoin: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: AxiosError<any> | null;
}) => {
  const [showError, setShowError] = useState<boolean>(false);

  const handleJoin = () => {
    onJoin();
  };

  const handleSetPin = (pin: string) => {
    setPin(pin);
    setShowError(false);
  };

  useEffect(() => {
    if (isError) setShowError(true);
  }, [isError]);

  return (
    <GameContainer>
      <JoinGameContainer>
        <h1 style={{ textAlign: 'center' }}>ENTER PIN</h1>
        <PinInput
          onPressEnter={() => handleJoin()}
          value={pin?.replace(/\s+/g, '')}
          onChange={handleSetPin}
        />
        {!isLoading && (
          <Button width="200px" onClick={() => handleJoin()} disabled={!pin}>
            Open moderation tool
          </Button>
        )}
        {isLoading && <Loader />}
        {error?.response?.status === 404 && showError && pin === lastSubmittedPin && (
          <ErrorText>
            <T
              keyName="The pin code could not be recognized Please try again"
              defaultValue="The pin code could not be recognized. Please try again"
            />
          </ErrorText>
        )}
        {error?.response?.status === 500 && showError && pin === lastSubmittedPin && (
          <ErrorText>
            <T
              keyName="Curipod server error occured Please try again"
              defaultValue="Curipod server error occured. Please try again"
            />
          </ErrorText>
        )}
        {error?.response &&
          error?.response?.status !== 404 &&
          error?.response?.status !== 500 &&
          showError &&
          pin === lastSubmittedPin && (
            <ErrorText>
              <T
                keyName="Unknown error occurred Please try again"
                defaultValue="Unknown error occurred. Please try again"
              />
            </ErrorText>
          )}
        {/* 
        When error.response is undefined and there is an error 
        - we have not gotten a response  
        from the server. Hence we display a message that should help the students
          tell the teacher that the it admins have to unblock curipod
        */}
        {error && error.response === undefined && showError && pin === lastSubmittedPin && (
          <ErrorText>
            <T
              keyName="Oh no! An unknown error occurred 😞"
              defaultValue="Oh no! An unknown error occurred 😞"
            />
          </ErrorText>
        )}
      </JoinGameContainer>
    </GameContainer>
  );
};

const ErrorText = styled.p`
  color: red;
  text-align: center;
`;

const GameContainer = styled.div`
  height: 100%;
  width: 100%;
  max-width: 450px;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
`;

const JoinGameContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const AppContainer = styled.div`
  height: 100%;
  min-height: 80vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  overflow-y: auto;
`;

const UnAuthenticated = () => {
  const { signIn } = useSignIn();

  useEffect(() => {
    signIn();
  }, [signIn]);
  return (
    <>
      <BackgroundBlobs />
    </>
  );
};

const ModeratePage = () => {
  const session = useSession();

  if (session.status === 'authenticated') {
    return <ControllerPage />;
  }
  return <UnAuthenticated />;
};

const UrlPinReader = ({
  pin,
  onPinRead,
}: {
  pin?: string;
  onPinRead: (code: string) => void;
}) => {
  const router = useRouter();
  const { pin: urlPin } = router.query;

  useEffect(() => {
    if (urlPin && !pin) {
      onPinRead(urlPin as string);
    }
  }, [pin, onPinRead, urlPin]);

  return null;
};

const ControllerPage = () => {
  const router = useRouter();
  const [pin, setPin] = useState<string>('');
  const [urlPin, setUrlPin] = useState<string>();
  const [lastSubmittedPin, setLastSubmittedPin] = useState<string>();
  const { tenantId } = useRouterMatch();

  const { data, error, isError, isLoading, mutate } = useMutation<
    {
      id: string;
      tenantId: string;
    },
    AxiosError<unknown>,
    string
  >(async (pin: string) => {
    const res = await backendService.instance.moderation.join(pin, tenantId);
    return res;
  });

  const handleJoin = useCallback(
    (pin: string) => {
      mutate(pin);
      setLastSubmittedPin(pin);
    },
    [mutate],
  );

  const handlePinRead = (pin: string) => {
    setUrlPin(pin);
  };

  useEffect(() => {
    if (urlPin && !isLoading && !data) {
      setUrlPin(undefined);
      setPin(urlPin);
      handleJoin(urlPin);
    }
  }, [urlPin, handleJoin, isLoading, data]);

  useEffect(() => {
    if (data) {
      router.push(`/${data.tenantId}/games/${data.id}/curimote`);
    }
  }, [data, router]);

  return (
    <LandingPageLayout
      title="The moderation tool"
      subTitle="Moderate your curipod lesson with the moderation tool"
      imageUrl="/moderation-tool.png"
      disableHeadline
    >
      <UrlPinReader pin={pin} onPinRead={handlePinRead} />
      <AppContainer>
        <Lobby
          isLoading={isLoading}
          isError={isError}
          error={error}
          onJoin={() => handleJoin(pin)}
          setPin={setPin}
          lastSubmittedPin={lastSubmittedPin}
          pin={pin}
        />
      </AppContainer>
    </LandingPageLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );

  const session = await getSession({ req: context.req });
  if (!session) {
    return {
      props: {
        ...(await detectAndBestEffortSetLanguage(context)),
      },
    };
  } else {
    const queryClient = new QueryClient();
    return {
      props: {
        ...(await provisionFlags(context, queryClient, session)),
        dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
        ...(await detectAndBestEffortSetLanguage(context)),
        session,
      },
    };
  }
};

export default ModeratePage;
